import { t, useLanguage, formatMoney, translateError } from "../../i18n";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { publicAPI, paymentAPI } from '../../services/api';
import { useCartStore, useOrderStore } from '../../store';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Clock3, CreditCard, AlertTriangle, Loader2, ReceiptText, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatOrderNumber } from '../../lib/utils';
const STATUS_CONFIG = {
  awaiting_payment: {
    get title() {
      return t("Paiement en attente");
    },
    get description() {
      return t("Votre commande n'a pas encore été transmise à l'établissement. Finalisez le paiement pour l'envoyer.");
    },
    icon: CreditCard,
    iconClassName: 'text-amber-600',
    boxClassName: 'bg-amber-50 border-amber-200 text-amber-900'
  },
  pending: {
    get title() {
      return t("Commande reçue");
    },
    get description() {
      return t("Votre commande a bien été transmise à l'établissement.");
    },
    icon: CheckCircle2,
    iconClassName: 'text-blue-600',
    boxClassName: 'bg-blue-50 border-blue-200 text-blue-900'
  },
  preparing: {
    get title() {
      return t("Commande en préparation");
    },
    get description() {
      return t("L'établissement prépare actuellement votre commande.");
    },
    icon: Clock3,
    iconClassName: 'text-amber-600',
    boxClassName: 'bg-amber-50 border-amber-200 text-amber-900'
  },
  ready: {
    get title() {
      return t("Commande prête");
    },
    get description() {
      return t("Votre commande est prête.");
    },
    icon: CheckCircle2,
    iconClassName: 'text-green-600',
    boxClassName: 'bg-green-50 border-green-200 text-green-900'
  },
  delivered: {
    get title() {
      return t("Commande livrée");
    },
    get description() {
      return t("Votre commande a été livrée.");
    },
    icon: CheckCircle2,
    iconClassName: 'text-slate-600',
    boxClassName: 'bg-slate-50 border-slate-200 text-slate-900'
  },
  cancelled: {
    get title() {
      return t("Commande annulée");
    },
    get description() {
      return t("Cette commande a été annulée.");
    },
    icon: AlertTriangle,
    iconClassName: 'text-red-600',
    boxClassName: 'bg-red-50 border-red-200 text-red-900'
  }
};
export default function OrderConfirmation() {
  useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id');
  const clearCart = useCallback(() => {
    try {
      const attempt = JSON.parse(sessionStorage.getItem('glassorder-checkout-attempt') || 'null');
      if (attempt?.order_id === orderId) {
        const submitted = JSON.parse(attempt.signature);
        const cart = useCartStore.getState();
        const currentItems = cart.items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          selected_options: i.selected_options || []
        }));
        if (cart.tableId === submitted.table_id && JSON.stringify(currentItems) === JSON.stringify(submitted.items)) cart.clearCart();
        sessionStorage.removeItem('glassorder-checkout-attempt');
      }
    } catch {/* Do not erase a cart when its payment context is unavailable. */}
  }, [orderId]);
  const setActiveOrder = useOrderStore(s => s.setActiveOrder);
  const [loading, setLoading] = useState(true);
  const [refreshingPayment, setRefreshingPayment] = useState(false);
  const [order, setOrder] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [resuming, setResuming] = useState(false);
  const currentStatus = order?.status || 'awaiting_payment';
  const statusConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.awaiting_payment;
  const canTreatAsConfirmed = useMemo(() => {
    if (!order) return false;
    return order.status !== 'awaiting_payment' || order.payment_status === 'paid';
  }, [order]);
  const addOrderToLocalTracking = useCallback(currentOrder => {
    if (!currentOrder?.id) return;
    if (localStorage.getItem('glassorder-pending-payment') === currentOrder.id) localStorage.removeItem('glassorder-pending-payment');
    const existingOrders = JSON.parse(localStorage.getItem('glassorder-active-orders') || '[]');
    const nextOrders = [{
      id: currentOrder.id,
      table_number: currentOrder.table_number,
      establishment_id: currentOrder.establishment_id,
      created_at: currentOrder.created_at || new Date().toISOString()
    }, ...existingOrders.filter(existingOrder => existingOrder.id !== currentOrder.id)];
    localStorage.setItem('glassorder-active-orders', JSON.stringify(nextOrders));
    setActiveOrder(currentOrder.id, currentOrder.status || 'pending', currentOrder.table_number);
  }, [setActiveOrder]);
  const loadOrder = useCallback(async () => {
    if (!orderId) return null;
    const response = await publicAPI.getOrder(orderId);
    setOrder(response.data);
    return response.data;
  }, [orderId]);
  useEffect(() => {
    if (!orderId || ['delivered', 'cancelled'].includes(currentStatus)) return;
    const timer = setInterval(() => {
      loadOrder().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [orderId, currentStatus, loadOrder]);
  const refreshPaymentAndOrder = useCallback(async ({
    silent = false
  } = {}) => {
    if (!orderId) return;
    try {
      if (!silent) {
        setRefreshingPayment(true);
      }
      if (sessionId) {
        const paymentResponse = await paymentAPI.getStatus(sessionId);
        setPaymentInfo(paymentResponse.data);
      }
      const freshOrder = await loadOrder();
      if (freshOrder && (freshOrder.status !== 'awaiting_payment' || freshOrder.payment_status === 'paid')) {
        clearCart();
        addOrderToLocalTracking(freshOrder);
      }
    } catch (err) {
      if (!silent) {
        toast.error(t("Impossible d'actualiser le paiement"));
      }
    } finally {
      if (!silent) {
        setRefreshingPayment(false);
      }
    }
  }, [orderId, sessionId, loadOrder, clearCart, addOrderToLocalTracking]);
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!orderId) {
        toast.error(t("Commande introuvable"));
        navigate('/menu');
        return;
      }
      try {
        if (sessionId) {
          for (let i = 0; i < 8; i += 1) {
            if (cancelled) return;
            const paymentResponse = await paymentAPI.getStatus(sessionId);
            setPaymentInfo(paymentResponse.data);
            const freshOrderResponse = await publicAPI.getOrder(orderId);
            const freshOrder = freshOrderResponse.data;
            setOrder(freshOrder);
            if (freshOrder.status !== 'awaiting_payment' || freshOrder.payment_status === 'paid') {
              clearCart();
              addOrderToLocalTracking(freshOrder);
              break;
            }
            if (paymentResponse.data?.payment_status !== 'paid') {
              await new Promise(resolve => setTimeout(resolve, 1500));
            }
          }
        } else {
          const freshOrder = await loadOrder();
          if (freshOrder && (freshOrder.status !== 'awaiting_payment' || freshOrder.payment_status === 'paid')) {
            clearCart();
            addOrderToLocalTracking(freshOrder);
          }
        }
      } catch (err) {
        toast.error(t("Erreur lors du chargement de la commande"));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [orderId, sessionId, navigate, clearCart, addOrderToLocalTracking, loadOrder]);
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB] p-6">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p>{t("Chargement de la commande...")}</p>
        </div>
      </div>;
  }
  if (!order) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB] p-6">
        <div className="text-center">
          <p className="mb-4 text-slate-500">{t("Commande introuvable")}</p>
          <Button onClick={() => navigate('/menu')}>{t("Retour au menu")}</Button>
        </div>
      </div>;
  }
  const StatusIcon = statusConfig.icon;
  return <div className="min-h-screen bg-[#F9FAFB] p-4 pb-10">
      <div className="mx-auto max-w-xl space-y-4">
        <button onClick={() => navigate('/menu')} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">

          <ArrowLeft className="h-4 w-4" />{t("Retour au menu")}

        </button>

        <div className={`rounded-3xl border p-6 ${statusConfig.boxClassName}`}>
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-white/70 p-3">
              <StatusIcon className={`h-7 w-7 ${statusConfig.iconClassName}`} />
            </div>

            <div className="flex-1">
              <h1 className="text-xl font-bold" style={{
              fontFamily: 'Manrope'
            }}>
                {statusConfig.title}
              </h1>

              <p className="mt-2 text-sm leading-6">
                {statusConfig.description}
              </p>

              {order.status === 'awaiting_payment' && <div className="mt-4 rounded-2xl border border-amber-200 bg-white/70 p-4 text-sm">
                  <p className="font-semibold text-amber-900">{t("Aucune commande n’a été envoyée au staff pour le moment.")}

                </p>

                  <p className="mt-1 text-amber-800">{t("Tant que le paiement n’est pas validé, la commande reste en attente.")}

                </p>
                </div>}
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900" style={{
            fontFamily: 'Manrope'
          }}>{t("Détails de la commande")}

            </h2>

            <span className="font-mono text-sm text-slate-500">
              N° {formatOrderNumber(order)}
            </span>
          </div>

          <div className="mt-4 space-y-3">
            {order.items?.map((item, index) => <div key={`${item.product_id}-${index}`} className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-900">
                    {item.quantity} × {item.name}
                  </p>

                  {!!item.selected_options?.length && <p className="mt-1 text-xs text-violet-500">
                      {item.selected_options.join(', ')}
                    </p>}
                </div>

                <span className="font-mono text-sm text-slate-700">
                  {item.price * item.quantity === 0 ? t("Gratuit") : formatMoney(item.price * item.quantity)}
                </span>
              </div>)}
          </div>

          <div className="mt-5 space-y-2 border-t border-slate-100 pt-4">
            {typeof order.subtotal === 'number' && <div className="flex items-center justify-between text-sm text-slate-500">
                <span>{t("Sous-total")}</span>
                <span className="font-mono">{formatMoney(order.subtotal)}</span>
              </div>}

            {!!order.promo_discount && <div className="flex items-center justify-between text-sm text-violet-600">
                <span>{t("Réduction")}</span>
                <span className="font-mono">-{formatMoney(order.promo_discount)}</span>
              </div>}

            <div className="flex items-center justify-between border-t border-slate-100 pt-2">
              <span className="font-semibold text-slate-900">{t("Total")}</span>

              <span className="font-mono text-xl font-bold text-slate-900">
                {Number(order.total_price || 0) === 0 ? t("Gratuit") : formatMoney(Number(order.total_price))}
              </span>
            </div>
          </div>
        </div>

        {sessionId && <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-slate-900">
              <ReceiptText className="h-4 w-4" />
              <h3 className="font-semibold">{t("Paiement")}</h3>
            </div>

            <div className="mt-3 space-y-1 text-sm text-slate-600">
              <p>{t("Statut Stripe :")}
              {' '}
                <span className="font-semibold text-slate-900">
                  {t(({ paid: 'Payé', unpaid: 'Paiement en attente', no_payment_required: 'Gratuit' })[paymentInfo?.payment_status] || 'Inconnu')}
                </span>
              </p>

              <p>{t("Session :")}
              <span className="font-mono">{sessionId.slice(0, 12)}...</span>
              </p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => refreshPaymentAndOrder()} disabled={refreshingPayment}>

                {refreshingPayment ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}{t("Actualiser le paiement")}

            </Button>

              {!canTreatAsConfirmed && <Button onClick={() => navigate('/cart')}>{t("Retour au panier")}

            </Button>}
            </div>
          </div>}

        <div className="flex flex-wrap gap-2">
          {order.status === 'awaiting_payment' && <Button disabled={resuming} onClick={async () => {
          setResuming(true);
          try {
            const {
              data
            } = await paymentAPI.createCheckout(order.id, window.location.origin);
            window.location.assign(data.url);
          } catch (error) {
            toast.error(translateError(error.response?.data?.detail, 'Impossible de reprendre le paiement'));
            setResuming(false);
            await loadOrder();
          }
        }} className="w-full bg-[#7C3AED]">{resuming ? t("Ouverture…") : t("Reprendre le paiement")}</Button>}
          {order.status === 'delivered' && <Button className="w-full" onClick={() => navigate(`/tip?order_id=${order.id}&table=${order.table_number}`)}>{t("Remercier l’équipe et donner mon avis")}</Button>}
          {order.cancellation_reason && <p className="w-full rounded-xl bg-amber-50 p-3">{t("Motif d’annulation :")} {order.cancellation_reason}</p>}
          <Button onClick={() => navigate('/my-orders')} className="min-w-[180px] flex-1">{t("Voir mes commandes")}

          </Button>

          <Button variant="outline" onClick={() => navigate('/menu')} className="min-w-[180px] flex-1">{t("Revenir au menu")}

          </Button>
        </div>
      </div>
    </div>;
}
